<template>
  <div
    role="main"
    aria-label="Création de paiement de colis"
    tabindex="-1"
  >
    <b-card
      class="top-card"
      body-class="p-2 pt-1 pb-2"
    >
      <!-- Incomplete Roadmaps Modal -->
      <b-modal
        id="incomplete-roadmaps-modal"
        ref="incompleteRoadmapsModal"
        title="Réclamations livreur non traitées"
        title-class="text-warning"
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        size="lg"
      >
        <div class="text-center">
          <feather-icon
            icon="AlertTriangleIcon"
            size="64"
            class="text-warning mb-3"
          />
          <h4 class="mb-3 text-warning">
            Impossible de créer le paiement
          </h4>

          <div class="alert alert-warning mb-4">
            <h5 class="alert-heading mb-3">
              <feather-icon
                icon="UserIcon"
                size="16"
                class="mr-1"
              />
              Chauffeur : <strong>{{ incompleteRoadmapData && incompleteRoadmapData.driverName }}</strong>
            </h5>
            <p class="mb-2">
              <strong>Problème détecté :</strong> Ce chauffeur a des réclamations livreur non traitées sur certaines feuilles de route.
            </p>
            <p class="mb-0">
              <strong>Feuilles de route concernées :</strong>
              <span
                v-for="id in (incompleteRoadmapData && incompleteRoadmapData.roadmapIds)"
                :key="id"
                class="badge badge-warning ml-1"
              >
                #{{ id }}
              </span>
            </p>
          </div>

          <div class="card border-info mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0">
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1"
                />
                Que faire pour résoudre ce problème ?
              </h6>
            </div>
            <div class="card-body text-left">
              <ol class="mb-0">
                <li class="mb-2">
                  <strong>Allez dans la section "Support" → "Réclamations Livreur"</strong>
                </li>
                <li class="mb-2">
                  <strong>Recherchez les réclamations</strong> pour les feuilles de route mentionnées ci-dessus
                </li>
                <li class="mb-2">
                  <strong>Traitez les réclamations</strong> en les marquant comme "Résolu" une fois le problème résolu
                </li>
                <li class="mb-2">
                  <strong>Marquez les feuilles de route comme "Terminées"</strong> si nécessaire
                </li>
                <li class="mb-0">
                  <strong>Revenez ici pour créer le paiement</strong> une fois toutes les réclamations traitées
                </li>
              </ol>
            </div>
          </div>

          <div class="alert alert-info">
            <small>
              <feather-icon
                icon="HelpCircleIcon"
                size="14"
                class="mr-1"
              />
              <strong>Pourquoi cette vérification ?</strong>
              Les réclamations livreur doivent être résolues avant le paiement. Une fois résolues, elles seront automatiquement fermées lors de la création du paiement.
            </small>
          </div>

          <div class="d-flex justify-content-center">
            <b-button
              variant="primary"
              @click="closeIncompleteRoadmapsModal"
            >
              <feather-icon
                icon="CheckIcon"
                size="16"
                class="mr-1"
              />
              J'ai compris
            </b-button>
          </div>
        </div>
      </b-modal>

      <!-- Confirmation Modal for Payment -->
      <b-modal
        ref="confirmationModal"
        title="Confirmation de paiement"
        size="lg"
        no-close-on-backdrop
        no-close-on-esc
        @ok="confirmAndValidate"
        @cancel="cancelPayment"
      >
        <template #modal-header="{ close }">
          <div class="w-100 d-flex justify-content-between align-items-center">
            <h4 class="modal-title mb-0">
              <feather-icon
                icon="CheckCircleIcon"
                class="mr-2"
              />
              Confirmation de paiement
            </h4>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="close()"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
        </template>

        <div class="payment-summary">
          <h5 class="mb-3">
            <feather-icon
              icon="UserIcon"
              class="mr-2"
            />
            Livreur: {{ delivery_man.username }}
          </h5>

          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-card
                variant="light"
                class="mb-3"
              >
                <h6 class="text-success mb-2">
                  <feather-icon
                    icon="CheckIcon"
                    class="mr-1"
                  />
                  Colis livrés
                </h6>
                <div class="d-flex justify-content-between">
                  <span>Nombre:</span>
                  <strong>{{ delivredOrders.length }}</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span>Total:</span>
                  <strong class="text-success">{{ deliveredTotalGross.toFixed(2) }} TND</strong>
                </div>
              </b-card>
              <!-- Driver Performance Card Feature -->
              <b-card
                v-if="delivery_man && driverPerformance.totalOrders > 0"
                class="mb-3 driver-performance-card"
                :border-variant="getPerformanceBorderVariant()"
                aria-label="Performance du livreur aujourd'hui"
              >
                <b-card-header class="pb-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">
                      <feather-icon
                        icon="TrendingUpIcon"
                        size="16"
                        class="mr-2"
                        :class="getPerformanceIconClass()"
                        aria-hidden="true"
                      />
                      Performance du livreur - {{ delivery_man.username }}
                    </h6>
                    <b-badge
                      :variant="getPerformanceBadgeVariant()"
                      class="badge-glow"
                    >
                      {{ driverPerformance.deliveryRate.toFixed(1) }}% livré
                    </b-badge>
                  </div>
                  <small class="text-muted">
                    Aperçu en temps réel basé sur les colis assignés aujourd'hui
                  </small>
                </b-card-header>
                <b-card-body class="pt-2">
                  <!-- Performance Overview -->
                  <b-row class="mb-3">
                    <b-col
                      cols="6"
                      md="3"
                    >
                      <div class="performance-stat text-center">
                        <div class="stat-icon mb-1">
                          <feather-icon
                            icon="PackageIcon"
                            size="20"
                            class="text-primary"
                          />
                        </div>
                        <div class="stat-value">
                          {{ driverPerformance.totalOrders }}
                        </div>
                        <div class="stat-label">
                          Total colis
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      cols="6"
                      md="3"
                    >
                      <div class="performance-stat text-center">
                        <div class="stat-icon mb-1">
                          <feather-icon
                            icon="CheckCircleIcon"
                            size="20"
                            class="text-success"
                          />
                        </div>
                        <div class="stat-value text-success">
                          {{ driverPerformance.deliveredCount }}
                        </div>
                        <div class="stat-label">
                          Livrés
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      cols="6"
                      md="3"
                    >
                      <div class="performance-stat text-center">
                        <div class="stat-icon mb-1">
                          <feather-icon
                            icon="ArrowLeftIcon"
                            size="20"
                            class="text-danger"
                          />
                        </div>
                        <div class="stat-value text-danger">
                          {{ driverPerformance.returnedCount }}
                        </div>
                        <div class="stat-label">
                          Retournés
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      cols="6"
                      md="3"
                    >
                      <div class="performance-stat text-center">
                        <div class="stat-icon mb-1">
                          <feather-icon
                            icon="ClockIcon"
                            size="20"
                            class="text-warning"
                          />
                        </div>
                        <div class="stat-value text-warning">
                          {{ driverPerformance.pendingCount }}
                        </div>
                        <div class="stat-label">
                          En cours
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                  <!-- Progress Bars -->
                  <div class="performance-progress mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <small class="text-muted">Taux de livraison</small>
                      <small class="font-weight-bold">{{ driverPerformance.deliveryRate.toFixed(1) }}%</small>
                    </div>
                    <b-progress
                      :value="driverPerformance.deliveryRate"
                      :variant="getDeliveryProgressVariant()"
                      height="8px"
                      class="mb-2"
                    />
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <small class="text-muted">Taux de retour</small>
                      <small class="font-weight-bold">{{ driverPerformance.returnRate.toFixed(1) }}%</small>
                    </div>
                    <b-progress
                      :value="driverPerformance.returnRate"
                      variant="danger"
                      height="8px"
                      class="mb-2"
                    />
                  </div>
                  <!-- Performance Indicators -->
                  <div class="performance-indicators">
                    <b-row>
                      <b-col
                        cols="12"
                        md="6"
                      >
                        <div class="performance-indicator d-flex align-items-center">
                          <feather-icon
                            :icon="getEfficiencyIcon()"
                            size="16"
                            :class="getEfficiencyIconClass()"
                            class="mr-2"
                          />
                          <span class="small">
                            <strong>Efficacité:</strong>
                            <span :class="getEfficiencyTextClass()">
                              {{ getEfficiencyLabel() }}
                            </span>
                          </span>
                        </div>
                      </b-col>
                      <b-col
                        cols="12"
                        md="6"
                      >
                        <div class="performance-indicator d-flex align-items-center">
                          <feather-icon
                            icon="DollarSignIcon"
                            size="16"
                            class="text-success mr-2"
                          />
                          <span class="small">
                            <strong>CA livré:</strong>
                            <span class="text-success">{{ driverPerformance.deliveredValue.toFixed(2) }} TND</span>
                          </span>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                  <!-- Exchange Orders Info (if any) -->
                  <div
                    v-if="driverPerformance.exchangeCount > 0"
                    class="exchange-info mt-2 p-2 bg-light rounded"
                  >
                    <small class="text-warning">
                      <feather-icon
                        icon="RefreshCwIcon"
                        size="14"
                        class="mr-1"
                      />
                      <strong>{{ driverPerformance.exchangeCount }} colis échangés</strong>
                      ({{ driverPerformance.exchangeValue.toFixed(2) }} TND)
                    </small>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <b-card
                variant="light"
                class="mb-3"
              >
                <h6 class="text-warning mb-2">
                  <feather-icon
                    icon="RefreshCwIcon"
                    class="mr-1"
                  />
                  Colis échangés
                </h6>
                <div class="d-flex justify-content-between">
                  <span>Nombre:</span>
                  <strong>{{ exchangeOrders.length }}</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span>Total:</span>
                  <strong class="text-warning">{{ exchangeTotalGross.toFixed(2) }} TND</strong>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-card
                variant="light"
                class="mb-3"
              >
                <h6 class="text-danger mb-2">
                  <feather-icon
                    icon="ArrowLeftIcon"
                    class="mr-1"
                  />
                  Colis retournés
                </h6>
                <div class="d-flex justify-content-between">
                  <span>Nombre:</span>
                  <strong>{{ returnOrders.length }}</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span>Total:</span>
                  <strong class="text-danger">{{ returnTotalGross.toFixed(2) }} TND</strong>
                </div>
              </b-card>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <b-card
                variant="primary"
                class="mb-3"
              >
                <h6 class="text-white mb-2">
                  <feather-icon
                    icon="DollarSignIcon"
                    class="mr-1"
                  />
                  Total frais de livraison
                </h6>
                <div class="d-flex justify-content-between text-white">
                  <span>Montant:</span>
                  <strong>{{ totalDeliveryPrice.toFixed(2) }} TND</strong>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <!-- Anomaly Orders in Confirmation Modal -->
          <b-row v-if="anomalyOrders.length > 0">
            <b-col cols="12">
              <b-alert
                show
                variant="danger"
                class="mb-3"
              >
                <h6 class="alert-heading mb-2">
                  <feather-icon
                    icon="AlertOctagonIcon"
                    size="16"
                    class="mr-1"
                  />
                  Anomalies de livraison détectées
                </h6>
                <div class="d-flex justify-content-between mb-2">
                  <span>Nombre d'anomalies:</span>
                  <strong>{{ anomalyOrders.length }}</strong>
                </div>
                <small class="d-block">
                  <strong>Colis concernés:</strong>
                  <span
                    v-for="order in anomalyOrders"
                    :key="order.id"
                    class="ml-1"
                  >
                    <b-badge
                      variant="danger"
                      class="mr-1"
                    >
                      {{ order.id }}
                    </b-badge>
                  </span>
                </small>
                <hr class="my-2">
                <small class="text-muted">
                  <feather-icon
                    icon="InfoIcon"
                    size="12"
                    class="mr-1"
                  />
                  Ces colis seront automatiquement marqués avec le statut "Anomalie de livraison"
                </small>
              </b-alert>
            </b-col>
          </b-row>

          <hr>

          <div class="total-payment">
            <h5 class="d-flex justify-content-between align-items-center">
              <span>
                <feather-icon
                  icon="CreditCardIcon"
                  class="mr-2"
                />
                Montant total du paiement:
              </span>
              <span class="text-primary font-weight-bold">
                {{ totalPaymentAmount.toFixed(2) }} TND
              </span>
            </h5>
          </div>

          <b-alert
            show
            variant="info"
            class="mt-3"
          >
            <feather-icon
              icon="InfoIcon"
              class="mr-2"
            />
            Êtes-vous sûr de vouloir valider ce paiement ? Cette action ne peut pas être annulée.
          </b-alert>
        </div>

        <template #modal-footer="{ ok, cancel }">
          <b-button
            variant="outline-secondary"
            @click="cancel()"
          >
            <feather-icon
              icon="XIcon"
              class="mr-1"
            />
            Annuler
          </b-button>
          <b-button
            variant="success"
            :disabled="isLoading"
            @click="ok()"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-1"
            />
            <feather-icon
              v-else
              icon="CheckIcon"
              class="mr-1"
            />
            {{ isLoading ? 'Traitement...' : 'Confirmer le paiement' }}
          </b-button>
        </template>
      </b-modal>

      <!-- <b-modal
        ref="returnReasonModal"
        title="Select Return Reasons"
        no-close-on-backdrop
        no-close-on-esc
        @ok="addReturnReasons"
      >
        <b-form-group
          label="Select Return Reason"
          label-for="returnReasonSelect"
        >
          <b-form-select
            id="returnReasonSelect"
            v-model="selectedReturnReason"
            :options="returnReasonTypes"
            value-field="id"
            text-field="content"
            label="Select Return Reason"
          />
        </b-form-group>
      </b-modal> -->
      <div
        v-if="isLoading === true"
        class="text-center mb-2"
      >
        <b-spinner
          variant="primary"
          label="Text Centered"
        />
      </div>
      <b-form-group
        label="Livreur (sélectionnez le chauffeur pour ce paiement)"
        aria-label="Sélection du livreur"
      >
        <validation-provider
          #default="{ errors }"
          name="Livreur"
        >
          <v-select
            v-model="delivery_man"
            :clearable="false"
            label="username"
            placeholder="Sélectionner un livreur professionnel"
            :options="delivery_mans"
            aria-label="Sélectionner un livreur pour le paiement"
            @input="getRoadmap"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>
      <!-- Enhanced Return Reason Selection -->
      <b-card
        class="mb-2 return-reason-card"
        :border-variant="selectedReturnReasonId ? 'success' : 'warning'"
        aria-label="Sélection du motif de retour"
      >
        <b-card-header class="pb-1 pt-2">
          <h6 class="mb-0">
            <feather-icon
              icon="AlertTriangleIcon"
              size="16"
              class="mr-1"
              :class="selectedReturnReasonId ? 'text-success' : 'text-warning'"
              aria-hidden="true"
            />
            Motif de retour (obligatoire pour l'ajout de colis aux retours)
          </h6>
          <small
            v-if="selectedReturnReasonContent"
            class="text-success"
            aria-live="polite"
          >
            Motif sélectionné : <strong>{{ selectedReturnReasonContent }}</strong>
          </small>
          <small
            v-else
            class="text-warning"
            aria-live="polite"
          >
            Aucun motif sélectionné – veuillez choisir un motif de retour pour continuer.
          </small>
        </b-card-header>
        <b-card-body class="pt-1 pb-1">
          <div class="return-reason-buttons">
            <b-button
              v-for="reason in returnReasonTypes"
              :key="reason.id"
              class="mr-1 mt-1 py-0"
              size="sm"
              :variant="selectedReturnReasonId === reason.id ? 'success' : 'outline-secondary'"
              @click="selectReturnReason(reason)"
            >
              <feather-icon
                v-if="selectedReturnReasonId === reason.id"
                icon="CheckIcon"
                size="14"
                class="mr-1"
              />
              {{ reason.content }}
            </b-button>
          </div>
        </b-card-body>
      </b-card>
      <b-form-group
        label="Référence de colis (scannez ou saisissez la référence)"
        label-for="vi-first-name"
        aria-label="Référence de colis à ajouter au paiement"
      >
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="MaximizeIcon"
              aria-hidden="true"
            />
          </b-input-group-prepend>
          <b-form-input
            v-model="ref"
            placeholder="Scanner ou saisir la référence du colis à retourner"
            aria-label="Champ pour scanner ou saisir la référence du colis à retourner"
            autofocus
            @change="checkOrder"
          />
        </b-input-group>
        <b-input-group class="input-group-merge mt-1">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="MaximizeIcon"
              aria-hidden="true"
            />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQueryForDeliveredOrder"
            placeholder="Scanner ou saisir la référence du colis livré"
            aria-label="Champ pour scanner ou saisir la référence du colis livré"
            @change="checkOrderDelivered"
          />
        </b-input-group>
        <b-input-group class="input-group-merge mt-1">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="AlertOctagonIcon"
              aria-hidden="true"
            />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQueryForAnomalyOrder"
            placeholder="Scanner ou saisir la référence du colis avec anomalie de livraison"
            aria-label="Champ pour scanner ou saisir la référence du colis avec anomalie"
            @change="checkOrderAnomaly"
          />
        </b-input-group>
        <small class="text-muted mt-1 d-block">
          <feather-icon
            icon="InfoIcon"
            size="14"
            class="mr-1"
          />
          <strong>Anomalie de livraison:</strong> Scannez les colis déjà retournés au dépôt pour les marquer comme anomalies
        </small>
      </b-form-group>
    </b-card>

    <!-- Show this message if no driver is selected -->
    <div
      v-if="!delivery_man"
      class="text-center my-5"
      aria-live="polite"
      role="alert"
    >
      <b-alert
        show
        variant="warning"
        class="py-4 no-driver-alert"
        aria-label="Alerte sélection livreur requise"
      >
        <feather-icon
          icon="UserIcon"
          size="32"
          class="mb-2"
          aria-hidden="true"
        />
        <div
          class="mt-2 font-weight-bold"
          style="font-size: 1.2rem;"
        >
          Veuillez sélectionner un livreur pour commencer le paiement.
        </div>
        <div class="text-muted mt-1">
          Aucun paiement n'est possible tant qu'aucun livreur n'est sélectionné.<br>
          Merci de choisir un livreur dans la liste ci-dessus.
        </div>
      </b-alert>
    </div>

    <!-- Main content is only shown if a driver is selected -->
    <div v-else>
      <!-- table -->
      <div
        v-if="orders.length > 0"
        class="d-flex justify-content-between"
      >
        <b-card>
          <h4> colis en cours : {{ orders.length }}</h4>
          <b-button
            class="btn btn-success mb-2"
            @click="deliverAll"
          >
            livrés tous les colis
          </b-button>
          <vue-good-table
            :columns="columns"
            :rows="orders"
            :search-options="{
              enabled: true
            }"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <div :class="{ 'permanent-return-row': isPermanentReturn(props.row) }">
                <span v-if="props.column.field === 'id'">
                  <span :class="{ 'text-danger font-weight-bold': isPermanentReturn(props.row) }"> {{ props.row.id }}</span><br>
                  <span
                    v-if="props.row.is_an_exchange"
                    style="color:red ; font-weight: bold;"
                  > Echange </span>
                  <!-- Display permanent return warning -->
                  <div
                    v-if="isPermanentReturn(props.row)"
                    class="permanent-return-info mt-2 p-2"
                    style="background-color: #fee; border: 2px solid #f00; border-radius: 4px; font-size: 0.85em;"
                  >
                    <div class="d-flex align-items-center mb-1">
                      <feather-icon
                        icon="XCircleIcon"
                        size="14"
                        class="text-danger mr-1"
                      />
                      <strong class="text-danger">RETOUR DÉFINITIF</strong>
                    </div>
                    <div class="permanent-return-details">
                      <div><strong>Motif:</strong> {{ props.row.permanent_return_reason }}</div>
                      <div v-if="props.row.permanent_return_decided_at">
                        <strong>Décidé le:</strong> {{ formatFn(props.row.permanent_return_decided_at) }}
                      </div>
                    </div>
                  </div>
                  <!-- Display temporary return reason if order has status 20 -->
                  <div
                    v-if="hasTemporaryReturnReason(props.row.id)"
                    class="temp-return-info mt-2 p-2"
                    style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px; font-size: 0.85em;"
                  >
                    <div class="d-flex align-items-center mb-1">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="14"
                        class="text-warning mr-1"
                      />
                      <strong class="text-warning">Problème livraison</strong>
                    </div>
                    <div class="temp-return-details">
                      <div><strong>Motif:</strong> {{ getTempReturnInfo(props.row.id).reason_type_content }}</div>
                      <div v-if="getTempReturnInfo(props.row.id).scheduled_date">
                        <strong>Date prévue:</strong> {{ formatTempReturnDate(getTempReturnInfo(props.row.id).scheduled_date) }}
                      </div>
                      <div v-if="getTempReturnInfo(props.row.id).additional_note">
                        <strong>Note:</strong> {{ getTempReturnInfo(props.row.id).additional_note }}
                      </div>
                    </div>
                  </div>
                </span>
                <!-- Column: Action -->
                <span v-else-if="props.column.field === 'action'">
                  <span>
                    <feather-icon
                      :id="`invoice-row-${props.row.id}-delivered-icon`"
                      icon="CheckIcon"
                      class="cursor-pointer mr-1"
                      size="16"
                      color="green"
                      @click="addItemToDeliveredList(props.row)"
                    />
                    <b-tooltip
                      :title="hasTemporaryReturnReason(props.row.id) ? 'Confirmer la livraison (scanner le colis)' : 'Livré'"
                      class="cursor-pointer"
                      :target="`invoice-row-${props.row.id}-delivered-icon`"
                    />
                  </span>
                  <!-- Hide return option for orders with temporary return reasons -->
                  <span
                    v-if="!hasTemporaryReturnReason(props.row.id)"
                    :class="(!selectedReturnReasonId || !selectedReturnReasonContent) ? 'disabled-cursor' : ''"
                  >
                    <feather-icon
                      :id="`invoice-row-${props.row.id}-return-icon`"
                      icon="CornerDownRightIcon"
                      class="mr-1 cursor-pointer"
                      size="16"
                      color="red"
                      :style="(!selectedReturnReasonId || !selectedReturnReasonContent) ? 'pointer-events: none; opacity: 0.5;' : ''"
                      @click="() => { if (selectedReturnReasonId && selectedReturnReasonContent) { addItemToReturnList(props.row) } }"
                    />
                    <b-tooltip
                      :title="!selectedReturnReasonId || !selectedReturnReasonContent ? 'Veuillez sélectionner un motif de retour avant d\'ajouter.' : 'Retour'"
                      class="cursor-pointer"
                      :target="`invoice-row-${props.row.id}-return-icon`"
                    />
                  </span>
                </span>

                <!-- Column: Common -->

                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </div>
            </template>

            <!-- pagination -->
            <template
              slot="pagination-bottom"
              slot-scope="props"
            >
              <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                  <span class="text-nowrap">
                    Affichage 1 à
                  </span>
                  <b-form-select
                    v-model="pageLength"
                    :options="['50','100','500']"
                    class="mx-1"
                    @input="(value)=>props.perPageChanged({currentPerPage:value})"
                  />
                  <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
                </div>
                <div>
                  <b-pagination
                    :value="1"
                    :total-rows="props.total"
                    :per-page="pageLength"
                    first-number
                    last-number
                    align="right"
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mt-1 mb-0"
                    @input="(value)=>props.pageChanged({currentPage:value})"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </div>
              </div>
            </template>
          </vue-good-table>
        </b-card>

      </div>

      <div
        v-if="delivredOrders.length > 0 || returnOrders.length > 0"
        class="d-flex justify-content-between"
      >
        <b-card>
          <h4> colis livré : {{ delivredOrders.length }}</h4>
          <vue-good-table
            :columns="columns"
            :rows="delivredOrders"
            :search-options="{
              enabled: true
            }"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.field === 'id'">
                <span> {{ props.row.id }}</span><br>
                <span
                  v-if="props.row.is_an_exchange"
                  style="color:red ; font-weight: bold;"
                > Echange </span>
              </span>
              <!-- Column: Action -->
              <span v-else-if="props.column.field === 'action'">
                <span>
                  <feather-icon
                    :id="`invoice-row-${props.row.id}-inprogress-icon`"
                    icon="CornerDownRightIcon"
                    class="cursor-pointer mr-1"
                    size="16"
                    color="red"
                    @click="addItemFromDeliveredToInProgressList(props.row)"
                  />
                  <b-tooltip
                    title="Colis en cours"
                    class="cursor-pointer"
                    :target="`invoice-row-${props.row.id}-inprogress-icon`"
                  />
                </span>
              </span>
              <!-- Column: Payment Method -->
              <span v-else-if="props.column.field === 'payment_method'">
                <b-badge
                  :variant="props.row.payment_method === '0' ? 'success' : (props.row.payment_method === '1' ? 'primary' : 'warning')"
                  class="px-2 py-1"
                >
                  {{ formatPaymentMethod(props.row.payment_method) }}
                </b-badge>
              </span>
              <!-- Column: Common -->
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>

            <!-- pagination -->
            <template
              slot="pagination-bottom"
              slot-scope="props"
            >
              <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                  <span class="text-nowrap">
                    Affichage 1 à
                  </span>
                  <b-form-select
                    v-model="pageLength"
                    :options="['50','100','500']"
                    class="mx-1"
                    @input="(value)=>props.perPageChanged({currentPerPage:value})"
                  />
                  <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
                </div>
                <div>
                  <b-pagination
                    :value="1"
                    :total-rows="props.total"
                    :per-page="pageLength"
                    first-number
                    last-number
                    align="right"
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mt-1 mb-0"
                    @input="(value)=>props.pageChanged({currentPage:value})"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </div>
              </div>
            </template>
          </vue-good-table>
        </b-card>

        <b-card class="ml-1">
          <h4> colis retour : {{ returnOrders.length }}</h4>
          <vue-good-table
            :columns="[...columns_return, { label: 'Dernier motif retour', field: 'last_return_reason' }]"
            :rows="returnOrders"
            :search-options="{
              enabled: true
            }"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.field === 'id'">
                <span> {{ props.row.id }}</span><br>
                <span
                  v-if="props.row.is_an_exchange"
                  style="color:red ; font-weight: bold;"
                > Echange </span>
              </span>
              <!-- Column: Status/Etat -->
              <span v-else-if="props.column.field === 'status'">
                <b-badge
                  :variant="getStatusVariant(props.row.status)"
                  class="px-2 py-1"
                >
                  {{ getStatusLabel(props.row.status) }}
                </b-badge>
              </span>
              <!-- Column: Dernier motif retour -->
              <span v-else-if="props.column.field === 'last_return_reason'">
                <div v-if="props.row.last_return_reason">
                  <strong class="d-block text-danger">
                    {{ props.row.last_return_reason.return_reason }}
                  </strong>
                  <small class="text-muted d-block">
                    {{ formatFn(props.row.last_return_reason.created_at) }}
                  </small>
                  <small
                    v-if="props.row.last_return_reason.scheduled_date"
                    class="text-info d-block"
                  >
                    Prévu: {{ formatFn(props.row.last_return_reason.scheduled_date) }}
                  </small>
                  <small
                    v-if="props.row.last_return_reason.additional_note"
                    class="text-secondary d-block"
                  >
                    {{ props.row.last_return_reason.additional_note }}
                  </small>
                </div>
                <span
                  v-else
                  class="text-muted"
                >-</span>
              </span>
              <span v-else-if="props.column.field === 'reasonTypeContent'">
                <!-- Enhanced inline editing for return reason -->
                <div class="return-reason-container">
                  <!-- Main return reason selector -->
                  <div class="d-flex align-items-center mb-1">
                    <b-form-select
                      :value="props.row.reasonTypeId"
                      :options="returnReasonOptions"
                      size="sm"
                      class="flex-grow-1"
                      @change="updateReturnReason(props.row, $event)"
                    />
                    <feather-icon
                      icon="EditIcon"
                      size="14"
                      class="ml-1 text-primary cursor-pointer"
                      :title="`Modifier le motif de retour pour le colis ${props.row.id}`"
                    />
                  </div>

                  <!-- Additional options based on return reason type -->
                  <div
                    v-if="needsDateTime(props.row.reasonTypeId)"
                    class="mt-1"
                  >
                    <b-input-group
                      size="sm"
                      :class="{ 'client-date-highlight': isClientDateReason(props.row.reasonTypeId) }"
                    >
                      <b-input-group-prepend>
                        <b-input-group-text
                          :class="{ 'bg-warning text-dark': isClientDateReason(props.row.reasonTypeId) }"
                        >
                          <feather-icon
                            :icon="isClientDateReason(props.row.reasonTypeId) ? 'CalendarIcon' : 'ClockIcon'"
                            size="12"
                          />
                        </b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input
                        :value="props.row.scheduledDate || getDefaultDateTime()"
                        type="datetime-local"
                        size="sm"
                        :placeholder="getDateTimePlaceholder(props.row.reasonTypeId)"
                        :class="{ 'border-warning': isClientDateReason(props.row.reasonTypeId) }"
                        :min="getMinDateTime()"
                        lang="fr-FR"
                        @input="updateScheduledDate(props.row, $event)"
                      />
                    </b-input-group>
                    <!-- Special note for client date requirement -->
                    <small
                      v-if="isClientDateReason(props.row.reasonTypeId)"
                      class="text-warning font-weight-bold"
                    >
                      <feather-icon
                        icon="AlertCircleIcon"
                        size="12"
                        class="mr-1"
                      />
                      Date demandée par le client - Requis
                    </small>
                  </div>

                  <!-- Additional text input -->
                  <div
                    v-if="needsAdditionalText(props.row.reasonTypeId)"
                    class="mt-1 return-reason-enhanced"
                    :class="{ 'client-date': isClientDateReason(props.row.reasonTypeId) }"
                  >
                    <label
                      class="form-label small text-muted mb-1"
                      :for="`additional-note-${props.row.id}`"
                    >
                      Note supplémentaire
                    </label>
                    <b-form-textarea
                      :id="`additional-note-${props.row.id}`"
                      :value="props.row.additionalNote"
                      size="sm"
                      rows="2"
                      :placeholder="getAdditionalTextPlaceholder(props.row.reasonTypeId)"
                      @input="updateAdditionalNote(props.row, $event)"
                    />
                    <!-- Debug info in development -->
                    <small
                      v-if="$parent.debug || false"
                      class="text-muted"
                    >
                      Current value: "{{ props.row.additionalNote }}"
                    </small>
                  </div>

                  <!-- Quick toggle for common additional options -->
                  <div
                    v-if="hasQuickOptions(props.row.reasonTypeId)"
                    class="mt-1"
                  >
                    <div class="d-flex flex-wrap">
                      <b-form-checkbox
                        v-for="option in getQuickOptions(props.row.reasonTypeId)"
                        :key="option.value"
                        :checked="isQuickOptionSelected(props.row, option.value)"
                        :value="option.value"
                        size="sm"
                        class="mr-2 mb-1"
                        @change="updateQuickOption(props.row, option.value, $event)"
                      >
                        {{ option.text }}
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>
              </span>
              <!-- Column: Action -->
              <span v-else-if="props.column.field === 'action'">
                <span>
                  <feather-icon
                    :id="`invoice-row-${props.row.id}-return-inprogress-icon`"
                    icon="DeleteIcon"
                    color="red"
                    class="cursor-pointer mr-1"
                    size="16"
                    @click="addItemFromReturnOrdersToInProgressList(props.row)"
                  />
                  <b-tooltip
                    title="Colis en cours"
                    class="cursor-pointer"
                    :target="`invoice-row-${props.row.id}-return-inprogress-icon`"
                  />
                </span>
              </span>
              <!-- Column: Common -->
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>

            <!-- pagination -->
            <template
              slot="pagination-bottom"
              slot-scope="props"
            >
              <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                  <span class="text-nowrap">
                    Affichage 1 à
                  </span>
                  <b-form-select
                    v-model="pageLength"
                    :options="['50','100','500']"
                    class="mx-1"
                    @input="(value)=>props.perPageChanged({currentPerPage:value})"
                  />
                  <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
                </div>
                <div>
                  <b-pagination
                    :value="1"
                    :total-rows="props.total"
                    :per-page="pageLength"
                    first-number
                    last-number
                    align="right"
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mt-1 mb-0"
                    @input="(value)=>props.pageChanged({currentPage:value})"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </div>
              </div>
            </template>

          </vue-good-table>
        </b-card>

      </div>
      <b-card
        no-body
        class="card-developer-meetup"
      >
        <div class="bg-light-primary rounded-top text-center">
          <b-img
            :src="require('@/assets/images/email.svg')"
            alt="Meeting Pic"
            height="170"
          />
        </div>
        <b-card-body>
          <!-- metting header -->
          <div class="meetup-header d-flex align-items-center">

            <div class="my-auto">
              <b-card-title class="mb-25">
                Paiement Livreur
              </b-card-title>
              <b-card-text class="mb-0">
                Total de tous les colis: <b-badge
                  pill
                  variant="primary"
                  class="badge-glow"
                >
                  {{ all }}
                </b-badge>
              </b-card-text>
            </div>
          </div>
          <!--/ metting header -->

          <!-- media -->
          <div class="d-flex flex-row align-items-center">
            <b-media
              class="mt-1"
              no-body
            >
              <b-media-aside class="mr-1">
                <b-avatar
                  rounded
                  variant="light-primary"
                  size="34"
                >
                  <feather-icon
                    icon="FolderPlusIcon"
                    size="18"
                    color="green"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="d-flex mt-1">
                <h6 class="mb-0">
                  Nombre de colis livré
                </h6>
                <div class="ml-2">
                  <feather-icon
                    icon="BellIcon"
                    size="25"
                    class="text-success "
                    :badge="delivredOrders.length"
                    badge-classes="badge-success badge-glow"
                  />
                </div>

              </b-media-body>
            </b-media>
            <b-media
              class="ml-5"
              no-body
            >
              <b-media-aside class="mr-1">
                <b-avatar
                  rounded
                  variant="light-primary"
                  size="34"
                >
                  <feather-icon
                    icon="DollarSignIcon"
                    size="18"
                    color="green"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="d-flex mt-1">
                <h6 class="mb-0">
                  Total de colis livré
                </h6>
                <div class="ml-2">
                  <b-badge
                    pill
                    variant="success"
                    class="badge-glow"
                  >
                    {{ deliveredTotalGross.toFixed(2) }} TND
                  </b-badge>
                </div>

              </b-media-body>
              <b-media-body
                v-if="user.role ==='admin'"
                class="d-flex mt-1"
              >
                <h6 class="mb-0">
                  Total de frais de livraison
                </h6>
                <div class="ml-2">
                  <b-badge
                    pill
                    variant="success"
                    class="badge-glow"
                  >
                    {{ totalDeliveryPrice.toFixed(2) }} TND
                  </b-badge>
                </div>

              </b-media-body>
            </b-media>

          </div>
          <div class="d-flex">
            <b-media
              class="mt-1"
              no-body
            >
              <b-media-aside class="mr-1">
                <b-avatar
                  rounded
                  variant="light-primary"
                  size="34"
                >
                  <feather-icon
                    icon="FolderMinusIcon"
                    size="18"
                    color="red"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="d-flex mt-1">
                <h6 class="mb-0">
                  Nombre de colis retour
                </h6>
                <div class="ml-1">
                  <feather-icon
                    icon="BellIcon"
                    size="25"
                    class="text-danger "
                    :badge="returnOrders.length"
                    badge-classes="badge-danger badge-glow"
                  />
                </div>

              </b-media-body>
            </b-media>
            <b-media
              class="ml-4"
              no-body
            >
              <b-media-aside class="mr-1">
                <b-avatar
                  rounded
                  variant="light-primary"
                  size="34"
                >
                  <feather-icon
                    icon="DollarSignIcon"
                    size="18"
                    color="red"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="d-flex mt-1">
                <h6 class="mb-0">
                  Total de colis retour
                </h6>
                <div class="ml-2">
                  <b-badge
                    pill
                    variant="danger"
                    class="badge-glow"
                  >
                    {{ returnTotalGross.toFixed(2) }} TND
                  </b-badge>
                </div>

              </b-media-body>
            </b-media>
            <b-media
              class="mt-1 ml-4"
              no-body
            >
              <b-media-aside class="mr-1">
                <b-avatar
                  rounded
                  variant="light-warning"
                  size="34"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="18"
                    color="orange"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="d-flex mt-1">
                <h6 class="mb-0">
                  Nombre des colis échange
                </h6>
                <div class="ml-1">
                  <feather-icon
                    icon="BellIcon"
                    size="25"
                    class="text-warning "
                    :badge="exchangeOrdersCount"
                    badge-classes="badge-warning badge-glow"
                  />
                </div>

              </b-media-body>
            </b-media>
          </div>

          <!-- Anomaly Orders Display -->
          <div
            v-if="anomalyOrders.length > 0"
            class="d-flex mt-2"
          >
            <b-media no-body>
              <b-media-aside class="mr-1">
                <b-avatar
                  rounded
                  variant="light-danger"
                  size="34"
                >
                  <feather-icon
                    icon="AlertOctagonIcon"
                    size="18"
                    color="red"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="d-flex mt-1">
                <h6 class="mb-0 text-danger">
                  Anomalies de livraison
                </h6>
                <div class="ml-2">
                  <b-badge
                    pill
                    variant="danger"
                    class="badge-glow"
                  >
                    {{ anomalyOrders.length }}
                  </b-badge>
                  <div class="mt-1">
                    <small
                      v-for="element in anomalyOrders"
                      :key="element.id"
                      class="d-block text-danger font-weight-bold"
                    >
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="12"
                        class="mr-1"
                      />
                      {{ element.id }}
                    </small>
                  </div>
                </div>
              </b-media-body>
            </b-media>
          </div>

          <b-form-group class=" mt-2">
            <b-button
              variant="gradient-primary"
              class="ml-1 d-inline-block"
              :class="{ 'disabled-cursor': isLoading || !isPaymentValid }"
              :disabled="isLoading || !isPaymentValid"
              @click="validate"
            >
              <feather-icon icon="CheckIcon" />
              <span class="ml-1">Valider paiement</span>
            </b-button>
          </b-form-group>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BFormSelect, BTooltip,
  BImg, BCardBody, BCardText, BCardTitle, BMedia, BMediaAside, BAvatar, VBTooltip, BMediaBody, BBadge,
  BFormTextarea, BModal, BRow, BCol, BAlert, BSpinner, BFormCheckbox, BCardHeader, BProgress,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import retour from '@/assets/audio/retour.mp3'
import livré from '@/assets/audio/livré.mp3'
import notexist from '@/assets/audio/notexist.mp3'
import retourDefinitif from '@/assets/audio/retour-definitif.mp3'
import anomalie from '@/assets/audio/error.mp3' // You can replace with custom 'anomalie.mp3' audio

export default {
  name: 'CreatePayment',
  components: {
    BImg,
    BBadge,
    BCardBody,
    BCardText,
    BMedia,
    BMediaAside,
    BAvatar,
    BTooltip,
    BMediaBody,
    BButton,
    ValidationProvider,
    VueGoodTable,
    BCardTitle,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BPagination,
    vSelect,
    BCard,
    BFormTextarea,
    BModal,
    BRow,
    BCol,
    BAlert,
    BSpinner,
    BFormCheckbox,
    BCardHeader,
    BProgress,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      all: 0,
      delivery_mans: [],
      delivery_man: '',
      status: '',
      pageLength: 100,
      dir: false,
      columns: [
        {
          label: 'BL',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'BL',
          },
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche gouvernorat',
          },
        },
        {
          label: 'Delegation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche delegation',
          },
        },
        {
          label: 'Telephone',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche telephone',
          },
        },
        {
          label: 'prix',
          field: 'price_ttc',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche prix',
          },
        },
        {
          label: 'Mode de paiement',
          field: 'payment_method',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche mode de paiement',
          },
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      columns_return: [
        {
          label: 'BL',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'BL',
          },
        },
        {
          label: 'Etat',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche état',
          },
        },
        {
          label: 'motif retour',
          field: 'reasonTypeContent',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche retour',
          },
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche gouvernorat',
          },
        },
        {
          label: 'Delegation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche delegation',
          },
        },
        {
          label: 'Telephone',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche telephone',
          },
        },
        {
          label: 'prix',
          field: 'price_ttc',
          filterOptions: {
            enabled: true,
            placeholder: 'recherche prix',
          },
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      role: '',
      orders: [],
      delivredOrders: [],
      returnOrders: [],
      anomalyOrders: [], // Store orders marked as delivery anomaly
      tempReturnInfo: [], // Store temporary return information for orders with status 20
      roadmapsId: [],
      searchTerm: '',
      ref: '',
      searchQueryForDeliveredOrder: '',
      searchQueryForAnomalyOrder: '',
      roadmaps: [],
      user: {},
      isLoading: false,
      retour,
      livré,
      notexist,
      retourDefinitif,
      anomalie,
      selectedReturnReason: {
        id: null,
        content: '',
      },
      selectedReturnReasons: [],
      returnReasonTypes: [],
      selectedReturnReasonId: null,
      selectedReturnReasonContent: null,
      incompleteRoadmaps: [],
      isCompletingRoadmaps: false,
      incompleteRoadmapData: null,
    }
  },
  computed: {
    // Backend calculates delivred_price as sum of ALL orders in delivred_orders array
    deliveredTotalGross() {
      return this.delivredOrders.reduce((accumulator, item) => accumulator + Number(item.price_ttc || 0), 0)
    },
    // Backend calculates return_price as sum of ALL orders in return_orders array
    returnTotalGross() {
      return this.returnOrders.reduce((accumulator, item) => accumulator + Number(item.price_ttc || 0), 0)
    },
    // Driver performance computed property
    driverPerformance() {
      if (!this.delivery_man) {
        return {
          totalOrders: 0,
          deliveredCount: 0,
          returnedCount: 0,
          pendingCount: 0,
          exchangeCount: 0,
          deliveryRate: 0,
          returnRate: 0,
          deliveredValue: 0,
          returnedValue: 0,
          exchangeValue: 0,
        }
      }

      const totalOrders = this.orders.length + this.delivredOrders.length + this.returnOrders.length
      const deliveredCount = this.delivredOrders.length
      const returnedCount = this.returnOrders.length
      const pendingCount = this.orders.length
      const exchangeCount = this.exchangeOrdersCount

      const deliveryRate = totalOrders > 0 ? (deliveredCount / totalOrders) * 100 : 0
      const returnRate = totalOrders > 0 ? (returnedCount / totalOrders) * 100 : 0

      return {
        totalOrders,
        deliveredCount,
        returnedCount,
        pendingCount,
        exchangeCount,
        deliveryRate,
        returnRate,
        deliveredValue: this.deliveredTotalGross,
        returnedValue: this.returnTotalGross,
        exchangeValue: this.exchangeTotalGross,
      }
    },
    totalDeliveryPrice() {
      // Backend calculates total_delivery_price from delivred_orders
      return this.delivredOrders.reduce((accumulator, item) => accumulator + Number(item.price_delivery || 0), 0)
    },
    isPaymentValid() {
      return (
        this.delivery_man !== '' && this.orders.length === 0
      )
    },
    exchangeOrders() {
      const paymentExchangeOrders = [
        ...this.delivredOrders,
        ...this.returnOrders,
      ].filter(order => order.is_an_exchange === true)

      return paymentExchangeOrders.filter((order, index, orders) => (
        orders.findIndex(existingOrder => existingOrder.id === order.id) === index
      ))
    },
    allExchangeOrders() {
      return this.exchangeOrders
    },
    exchangeOrdersCount() {
      return this.allExchangeOrders.length
    },
    returnReasonOptions() {
      return this.returnReasonTypes.map(reason => ({
        value: reason.id,
        text: reason.content,
      }))
    },
    exchangeTotalGross() {
      return this.exchangeOrders.reduce((accumulator, item) => accumulator + Number(item.price_ttc || 0), 0)
    },
    totalPaymentAmount() {
      // Total payment = delivered orders total only
      // price_ttc already includes delivery/shipment price
      return this.deliveredTotalGross
    },
  },
  created() {
    this.user = storeAuth.state.user

    this.getDeliveryMans()
    this.getReturnReasonTypes()
  },
  methods: {
    // Performance badge and styling methods
    getPerformanceBorderVariant() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'success'
      if (rate >= 60) return 'warning'
      return 'danger'
    },

    getPerformanceBadgeVariant() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'success'
      if (rate >= 60) return 'warning'
      return 'danger'
    },

    getPerformanceIconClass() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'text-success'
      if (rate >= 60) return 'text-warning'
      return 'text-danger'
    },

    getDeliveryProgressVariant() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'success'
      if (rate >= 60) return 'warning'
      return 'danger'
    },

    // Efficiency indicators
    getEfficiencyIcon() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'TrendingUpIcon'
      if (rate >= 60) return 'MinusIcon'
      return 'TrendingDownIcon'
    },

    getEfficiencyIconClass() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'text-success'
      if (rate >= 60) return 'text-warning'
      return 'text-danger'
    },

    getEfficiencyTextClass() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 80) return 'text-success font-weight-bold'
      if (rate >= 60) return 'text-warning font-weight-bold'
      return 'text-danger font-weight-bold'
    },

    getEfficiencyLabel() {
      const rate = this.driverPerformance.deliveryRate
      if (rate >= 90) return 'Excellente'
      if (rate >= 80) return 'Très bonne'
      if (rate >= 70) return 'Bonne'
      if (rate >= 60) return 'Moyenne'
      if (rate >= 40) return 'Faible'
      return 'Très faible'
    },
    formatPaymentMethod(paymentMethod) {
      const paymentMethods = {
        0: 'En Espèces',
        1: 'Par chèque',
        2: 'Par traite',
      }
      return paymentMethods[paymentMethod] || 'Non défini'
    },
    selectReturnReason(reason) {
      this.selectedReturnReasonId = reason.id
      this.selectedReturnReasonContent = reason.content
    },
    updateReturnReason(orderItem, newReasonId) {
      // Find the new reason details
      const newReason = this.returnReasonTypes.find(reason => reason.id === parseInt(newReasonId, 10))
      if (newReason) {
        // Find the order in returnOrders array and update it
        const orderIndex = this.returnOrders.findIndex(order => order.id === orderItem.id)
        if (orderIndex !== -1) {
          // Update the order item with new return reason using Vue.set for reactivity
          this.$set(this.returnOrders[orderIndex], 'reasonTypeId', newReason.id)
          this.$set(this.returnOrders[orderIndex], 'reasonTypeContent', newReason.content)

          // Show success message
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Motif de retour mis à jour',
              text: `Le motif de retour pour le colis ${orderItem.id} a été mis à jour vers: ${newReason.content}`,
              variant: 'success',
            },
          })
        }
      }
    },
    // Helper methods for enhanced return reason UI
    isClientDateReason(reasonTypeId) {
      // Use require_planification instead of string matching
      const reason = this.returnReasonTypes.find(r => r.id === reasonTypeId)
      return reason && reason.require_planification
    },
    needsDateTime(reasonTypeId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonTypeId)
      return reason && reason.require_datetime
    },
    needsAdditionalText(reasonTypeId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonTypeId)
      return reason && reason.require_additional_note
    },
    hasQuickOptions() {
      // If you add a has_quick_options field, use it here
      // Example: return reason && reason.has_quick_options
      // For now, fallback to always false unless you add such a field
      return false
    },
    getDateTimePlaceholder(reasonTypeId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonTypeId)
      if (!reason) return 'Date et heure prévue'
      // Use backend-provided placeholder if available, else fallback
      return reason.datetime_placeholder || 'Date et heure prévue'
    },
    getAdditionalTextPlaceholder(reasonTypeId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonTypeId)
      if (!reason) return 'Ajouter des détails...'
      // Use backend-provided placeholder if available, else fallback
      return reason.additional_note_placeholder || 'Ajouter des détails supplémentaires...'
    },
    getQuickOptions(reasonTypeId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonTypeId)
      // Use backend-provided quick_options if available, else fallback to []
      return (reason && reason.quick_options) ? reason.quick_options : []
    },
    updateReturnReasonDetails(orderItem) {
      // This method is called when additional details are updated
      // It ensures the order data is saved properly using Vue's reactivity system
      const orderIndex = this.returnOrders.findIndex(order => order.id === orderItem.id)
      if (orderIndex !== -1) {
        // Update the specific fields to ensure reactivity
        this.$set(this.returnOrders[orderIndex], 'scheduledDate', orderItem.scheduledDate)
        this.$set(this.returnOrders[orderIndex], 'additionalNote', orderItem.additionalNote)
        this.$set(this.returnOrders[orderIndex], 'quickOptions', orderItem.quickOptions)
      }
    },
    updateScheduledDate(orderItem, newDate) {
      const orderIndex = this.returnOrders.findIndex(order => order.id === orderItem.id)
      if (orderIndex !== -1) {
        let finalDate = newDate
        if (newDate && !newDate.includes('T')) {
          finalDate = `${newDate}T08:00`
        }
        this.$set(this.returnOrders[orderIndex], 'scheduledDate', finalDate)
      }
    },
    updateAdditionalNote(orderItem, newNote) {
      const orderIndex = this.returnOrders.findIndex(order => order.id === orderItem.id)
      if (orderIndex !== -1) {
        this.$set(this.returnOrders[orderIndex], 'additionalNote', newNote)
      }
    },
    updateQuickOption(orderItem, optionValue, isSelected) {
      const orderIndex = this.returnOrders.findIndex(order => order.id === orderItem.id)
      if (orderIndex !== -1) {
        if (!this.returnOrders[orderIndex].quickOptions) {
          this.$set(this.returnOrders[orderIndex], 'quickOptions', [])
        }
        let quickOptions = [...this.returnOrders[orderIndex].quickOptions]
        if (isSelected && !quickOptions.includes(optionValue)) {
          quickOptions.push(optionValue)
        } else if (!isSelected && quickOptions.includes(optionValue)) {
          quickOptions = quickOptions.filter(option => option !== optionValue)
        }
        this.$set(this.returnOrders[orderIndex], 'quickOptions', quickOptions)
      }
    },
    isQuickOptionSelected(orderItem, optionValue) {
      return orderItem.quickOptions && orderItem.quickOptions.includes(optionValue)
    },
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async speakText(text) {
      // Use Web Speech API for Text-to-Speech (no package needed!)
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'fr-FR' // French language
        utterance.rate = 1.0 // Normal speed
        utterance.pitch = 1.0 // Normal pitch
        utterance.volume = 1.0 // Maximum volume
        window.speechSynthesis.speak(utterance)
      } else {
        console.warn('Speech Synthesis not supported in this browser')
      }
    },

    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },

    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },

    async checkOrder() {
      const result = this.orders.find(element => orderMatchesReference(element, this.ref))
      if (result) {
        // Check if this is a permanent return order
        if (this.isPermanentReturn(result)) {
          // Play permanent return audio and show alert
          this.playAudio(retourDefinitif)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'RETOUR DÉFINITIF',
              text: `Colis ${result.id} - Ce colis est marqué comme retour définitif. Motif: ${result.permanent_return_reason}`,
              variant: 'danger',
            },
          })
        }
        // this.openReturnReasonModal(result)
        result.reasonTypeContent = this.selectedReturnReasonContent
        await this.addItemToReturnList(result)
        result.reasonTypeId = this.selectedReturnReasonId
        this.ref = ''
        // Play regular return audio if not permanent return
        if (!this.isPermanentReturn(result)) {
          // this.playAudio(retour)
          // Speak return with motif
          this.speakText(`Retour au dépôt. ${this.selectedReturnReasonContent}`)
        }
      } else {
        await this.withTitle('Colis non exist')
        this.playAudio(notexist)
      }
    },
    async checkOrderDelivered() {
      const result = this.orders.find(element => orderMatchesReference(element, this.searchQueryForDeliveredOrder))
      if (result) {
        await this.addItemToDeliveredList(result)
        this.searchQueryForDeliveredOrder = ''
        this.playAudio(livré)
      } else {
        await this.withTitle('Colis non exist')
        this.playAudio(notexist)
      }
    },
    async checkOrderAnomaly() {
      // Look for the order in the return orders list (status 5 - RETOUR_AU_DEPOT)
      console.log('Searching for anomaly order:', this.searchQueryForAnomalyOrder)
      console.log('Return orders:', this.returnOrders.map(o => ({ id: o.id, status: o.status })))

      const orderIndex = this.returnOrders.findIndex(element => orderMatchesReference(element, this.searchQueryForAnomalyOrder))
      console.log('Found order at index:', orderIndex)

      if (orderIndex !== -1) {
        const result = this.returnOrders[orderIndex]

        // Check if already in anomaly list
        const alreadyAnomaly = this.anomalyOrders.find(order => order.id === result.id)
        if (alreadyAnomaly) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Déjà scanné',
              text: `Le colis ${result.id} est déjà dans la liste des anomalies`,
              variant: 'warning',
            },
          })
        } else {
          // Update the order status to DELIVERY_ANOMALY (24) in the returnOrders array
          console.log('Updating status from', result.status, 'to 24')
          this.$set(this.returnOrders[orderIndex], 'status', 24)

          // Also update the result object status before pushing
          result.status = 24

          // Add to anomaly orders list
          this.anomalyOrders.push(result)

          console.log('Status after update:', this.returnOrders[orderIndex].status)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Anomalie de livraison',
              text: `Colis ${result.id} marqué comme anomalie de livraison`,
              variant: 'danger',
            },
          })
          // Speak "Anomalie" with the return reason/motif
          const motif = result.reasonTypeContent || 'Motif non spécifié'
          this.speakText(`Anomalie. ${motif}`)
        }
        this.searchQueryForAnomalyOrder = ''
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Colis non trouvé',
            text: 'Ce colis n\'est pas dans la liste des retours au dépôt',
            variant: 'warning',
          },
        })
        this.playAudio(notexist)
        this.searchQueryForAnomalyOrder = ''
      }
    },
    async validate() {
      // Validate all return orders have return reasons
      const invalidReturnOrders = this.returnOrders.filter(order => !order.reasonTypeId || !order.reasonTypeContent)

      if (invalidReturnOrders.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Motifs de retour manquants',
            text: `${invalidReturnOrders.length} colis de retour n'ont pas de motif spécifié. Veuillez ajouter des motifs de retour pour tous les colis.`,
            variant: 'danger',
          },
        })
        return
      }

      // Show confirmation modal instead of immediately processing payment
      this.$refs.confirmationModal.show()
    },
    async confirmAndValidate() {
      // This method is called when user confirms in the modal
      // Simplified payload - only send essential data
      const obj = {
        roadmaps: this.roadmapsId,
        delivred_orders: this.delivredOrders.map(order => order.id),
        return_orders: this.returnOrders, // Send full return order objects as expected by backend
        exchange_orders: this.exchangeOrders.map(order => order.id),
        anomaly_orders: this.anomalyOrders.map(order => order.id), // Add anomaly orders
        delivery_man: this.delivery_man.id,
      }

      try {
        this.isLoading = true
        const response = await axios.post('/api/payments/create-payment/', obj)
        const paymentId = response.data

        // Show success message
        this.showToast('success', 'top-center', 'Paiement effectué avec succès')

        // Collect ONLY exchange orders for printing
        // Filter delivered orders to only include those that are exchanges
        const deliveredExchangeOrders = this.delivredOrders
          .filter(order => order.is_an_exchange === true)
          .map(order => order.id)

        // Exchange orders are already exchanges by definition
        const exchangeOrderIds = this.exchangeOrders.map(order => order.id)

        // Combine both lists and remove duplicates
        const allExchangeOrders = [...new Set([...deliveredExchangeOrders, ...exchangeOrderIds])]

        // If there are any exchange orders to print, redirect to bulk print page
        if (allExchangeOrders.length > 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Impression des bons d\'échange',
              text: `Redirection vers l'impression de ${allExchangeOrders.length} bon(s) d'échange`,
              variant: 'info',
            },
          })

          // Redirect to bulk order print page
          setTimeout(() => {
            this.redirectToBulkOrderPrint(allExchangeOrders)
          }, 1000)
        } else {
          this.$router.push(`/payments/show/${paymentId}`)
        }
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.showToast('danger', 'top-center', error.toString())
      }
    },
    cancelPayment() {
      // This method is called when user cancels in the modal
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Paiement annulé',
          text: 'Le processus de paiement a été annulé.',
          variant: 'info',
        },
      })
    },
    redirectToBulkOrderPrint(orderIds) {
      /**
       * Redirect to bulk print page for orders (delivered + exchange)
       */
      try {
        if (!orderIds || orderIds.length === 0) {
          console.warn('No orders to print')
          return
        }

        // Join order IDs as comma-separated string
        const ids = orderIds.join(',')

        // Redirect to bulk order print page with order IDs
        this.$router.push({
          name: 'order-echange-bulk-show',
          query: { ids },
        })
      } catch (error) {
        console.error('Error redirecting to bulk print:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Impossible de charger la page d\'impression.',
            variant: 'warning',
          },
        })
      }
    },
    async getDeliveryMans() {
      this.isLoading = true
      const { data } = await axios.get('/api/auth/drivers/eligible-for-payment/')
      this.delivery_mans = data
      this.isLoading = false
    },
    async getReturnReasonTypes() {
      const { data } = await axios.get('/api/return-vouchers/reason-types/')
      this.returnReasonTypes = data.results || data
    },
    async addItemToReturnList(item) {
      const hasTemporaryReturn = this.hasTemporaryReturnReason(item.id)
      const existingReturnReason = item.last_return_reason || {}
      const existingReasonContent = item.reasonTypeContent
        || existingReturnReason.return_reason
        || existingReturnReason.reason_type_content
        || ''
      const existingReasonTypeId = item.reasonTypeId
        || existingReturnReason.reason_type_id
        || existingReturnReason.reasonTypeId
        || null
      const hasExistingReturnReason = Boolean(existingReasonTypeId && existingReasonContent)

      if (!hasTemporaryReturn && !hasExistingReturnReason && (!this.selectedReturnReasonId || !this.selectedReturnReasonContent)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Motif de retour requis',
            text: 'Veuillez sélectionner un motif de retour avant d\'ajouter un colis à la liste de retour.',
            variant: 'danger',
          },
        })
        return
      }

      const returnItem = { ...item }

      // Set status to RETOUR_AU_DEPOT (5) for display in the table
      returnItem.status = 5

      // Check if order has temporary return reason
      const tempReturnInfo = this.getTempReturnInfo(item.id)

      if (tempReturnInfo) {
        // Use temporary return reason data
        returnItem.reasonTypeContent = tempReturnInfo.reason_type_content
        returnItem.reasonTypeId = tempReturnInfo.reason_type_id
        returnItem.scheduledDate = this.convertToDateTimeLocal(tempReturnInfo.scheduled_date)
        returnItem.additionalNote = tempReturnInfo.additional_note || ''
        returnItem.quickOptions = []
      } else if (hasExistingReturnReason) {
        returnItem.reasonTypeContent = existingReasonContent
        returnItem.reasonTypeId = existingReasonTypeId
        returnItem.scheduledDate = this.convertToDateTimeLocal(item.scheduledDate || existingReturnReason.scheduled_date)
        returnItem.additionalNote = item.additionalNote || existingReturnReason.additional_note || ''
        returnItem.quickOptions = item.quickOptions || existingReturnReason.quick_options || []
      } else {
        // Use selected return reason
        returnItem.reasonTypeContent = this.selectedReturnReasonContent
        returnItem.reasonTypeId = this.selectedReturnReasonId
        returnItem.scheduledDate = null
        returnItem.additionalNote = ''
        returnItem.quickOptions = []
      }

      this.returnOrders.push(returnItem)

      const i = this.orders.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })
      this.orders.splice(i, 1)

      // Show success message
      const reasonText = tempReturnInfo ? tempReturnInfo.reason_type_content : this.selectedReturnReasonContent
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Colis ajouté aux retours',
          text: `Le colis ${item.id} a été ajouté aux retours avec le motif: ${reasonText}`,
          variant: 'success',
        },
      })
    },
    async addItemToDeliveredList(item) {
      // For orders with temporary return reasons, this represents scanning and confirming delivery
      const tempReturnInfo = this.getTempReturnInfo(item.id)

      if (tempReturnInfo) {
        // Show confirmation message for temporary return orders
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Colis scanné et confirmé',
            text: `Le colis ${item.id} avec retour temporaire (${tempReturnInfo.reason_type_content}) a été scanné et confirmé comme livré.`,
            variant: 'success',
          },
        })

        // Remove from temporary return info since it's now confirmed as delivered
        this.tempReturnInfo = this.tempReturnInfo.filter(info => info.order_id !== item.id)
      }

      // add the item to delivredOrders list
      this.delivredOrders.push(item)

      // search the index of the item that need to be removed from the orders list
      const i = this.orders.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })

      // remove the searched item from orders list
      this.orders.splice(i, 1)
    },
    async addItemFromDeliveredToInProgressList(item) {
      // add the item to orders list
      this.orders.push(item)

      // search the index of the item that need to be removed from the delivredOrders list
      const i = this.delivredOrders.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })

      // remove the searched item from delivredOrders list
      this.delivredOrders.splice(i, 1)
    },
    async addItemFromReturnOrdersToInProgressList(item) {
      this.orders.push(item)

      const i = this.returnOrders.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })
      this.returnOrders.splice(i, 1)
    },
    async deliverAll() {
      this.delivredOrders = [...this.delivredOrders, ...this.orders]
      this.orders = []
    },
    async getRoadmap() {
      this.roadmaps = []
      this.roadmapsId = []
      this.orders = []
      this.delivredOrders = []
      this.returnOrders = [] // Always keep empty as requested
      this.tempReturnInfo = [] // Clear temporary return information

      try {
        // First, get roadmaps with cache-busting timestamp to avoid stale data
        const timestamp = new Date().getTime()
        const response = await axios.get('api/roadmaps/', {
          params: {
            delivery_man: this.delivery_man.id,
            is_payed: false,
            _t: timestamp, // Cache-busting parameter
          },
        })
        this.roadmaps = response.data

        await this.loadOrdersFromRoadmaps()
      } catch (error) {
        // Check if error is due to incomplete roadmaps
        if (error.response && error.response.status === 400 && error.response.data.error === 'incomplete_roadmaps') {
          // Store incomplete roadmap data for modal
          this.incompleteRoadmapData = {
            driverName: this.delivery_man.username,
            roadmapIds: error.response.data.roadmap_ids,
          }

          // Show incomplete roadmaps modal
          this.$refs.incompleteRoadmapsModal.show()

          // Reset driver selection
          this.delivery_man = null
          this.roadmaps = []
          this.incompleteRoadmaps = []
          return // Block payment creation
        }

        // Handle other errors
        this.orders = []
        this.delivredOrders = []
        this.returnOrders = []
      }
    },

    async loadOrdersFromRoadmaps() {
      if (this.roadmaps.length !== 0) {
        for (let i = 0; i < this.roadmaps.length; i += 1) {
          this.roadmapsId.push(this.roadmaps[i].id)

          for (let e = 0; e < this.roadmaps[i].orders.length; e += 1) {
            const order = this.roadmaps[i].orders[e]

            // Check order status and add to corresponding arrays
            if (order.status === 3) {
              this.orders.push(order)
            } else if (order.status === 4) {
              this.delivredOrders.push(order)
            } else if (order.status === 5) {
              this.returnOrders.push(order)
            }
          }
        }
      }

      // Additionally, fetch orders by status for auto-population
      await this.getDriverOrdersByStatus()

      // Calculate total
      this.all = this.delivredOrders.reduce((accumulator, item) => accumulator + Number(item.price_ttc), 0)
    },

    async confirmRoadmapCompletion() {
      this.isCompletingRoadmaps = true

      try {
        // Complete all incomplete roadmaps using the same endpoint as in roadmaps list
        await Promise.all(
          this.incompleteRoadmaps.map(roadmap => axios.post(`/api/roadmaps/toggle-completion/${roadmap.id}/`, {
            force_complete: true,
          })),
        )

        // Show success message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Feuilles de route terminées',
            text: `${this.incompleteRoadmaps.length} feuille(s) de route marquée(s) comme terminée(s)`,
            variant: 'success',
          },
        })

        // Wait a moment for database to update, then reload roadmaps
        await new Promise(resolve => setTimeout(resolve, 500))

        // Reload roadmaps and continue with payment creation
        const timestamp = new Date().getTime()
        const response = await axios.get('api/roadmaps/', {
          params: {
            delivery_man: this.delivery_man.id,
            is_payed: false,
            _t: timestamp, // Cache-busting parameter
          },
        })
        this.roadmaps = response.data
        this.incompleteRoadmaps = []

        await this.loadOrdersFromRoadmaps()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Erreur lors de la finalisation des feuilles de route',
            variant: 'danger',
          },
        })
      } finally {
        this.isCompletingRoadmaps = false
      }
    },

    cancelRoadmapCompletion() {
      // Reset driver selection
      this.delivery_man = null
      this.incompleteRoadmaps = []
    },
    async getDriverOrdersByStatus() {
      if (!this.delivery_man || !this.delivery_man.id) {
        return
      }

      try {
        const response = await axios.get(`/api/payments/driver-orders/${this.delivery_man.id}/`)
        const {
          orders, deliveredOrders, tempReturnInfo,
        } = response.data

        // Store temporary return information for display
        this.tempReturnInfo = tempReturnInfo || []

        // Merge with existing orders from roadmaps (avoid duplicates)
        const existingOrderIds = new Set([
          ...this.orders.map(o => o.id),
          ...this.delivredOrders.map(o => o.id),
          ...this.returnOrders.map(o => o.id),
        ])

        // Add new orders that aren't already in the lists
        // Note: orders now includes both status 3 (EN_COURS) and status 20 (RETOUR_TEMPORAIRE)
        orders.forEach(order => {
          if (!existingOrderIds.has(order.id)) {
            this.orders.push(order)
          }
        })

        deliveredOrders.forEach(order => {
          if (!existingOrderIds.has(order.id)) {
            this.delivredOrders.push(order)
          }
        })

        // returnOrders should always be empty as requested
        // Temporary return orders (status 20) are now included in the 'orders' array
        // and will be displayed in the "colis en cours" table with their reasons below
      } catch (error) {
        console.error('Error fetching driver orders by status:', error)
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    getMinDateTime() {
      // Set minimum date to today at 8:00 AM
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 0)

      // If current time is after 8 AM, use current time, otherwise use 8 AM
      const minTime = now > today ? now : today

      // Format for datetime-local input (YYYY-MM-DDTHH:MM)
      return minTime.toISOString().slice(0, 16)
    },
    getDefaultDateTime() {
      // Default to tomorrow at 8:00 AM
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(8, 0, 0, 0)

      // Format for datetime-local input (YYYY-MM-DDTHH:MM)
      return tomorrow.toISOString().slice(0, 16)
    },
    getTempReturnInfo(orderId) {
      // Get temporary return information for a specific order
      return this.tempReturnInfo.find(info => info.order_id === orderId) || null
    },
    hasTemporaryReturnReason(orderId) {
      // Check if an order has a temporary return reason
      return this.getTempReturnInfo(orderId) !== null
    },
    formatTempReturnDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return ''
      return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    },
    convertToDateTimeLocal(dateString) {
      if (!dateString) return null
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return null

      // Convert to local datetime format (YYYY-MM-DDTHH:MM) for datetime-local input
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    closeIncompleteRoadmapsModal() {
      this.$refs.incompleteRoadmapsModal.hide()
      this.incompleteRoadmapData = null
    },
    isPermanentReturn(order) {
      // Check if order is marked as permanent return
      return order && order.permanent_return_reason && order.permanent_return_reason.trim() !== ''
    },
    getStatusLabel(status) {
      // Map order status numbers to French labels
      const statusMap = {
        0: 'En Attente',
        1: 'En Pickup',
        2: 'Colis au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour au fournisseur',
        8: 'En Transfert',
        9: 'Retour payé',
        10: 'Retour inter agence',
        11: 'Retour inter agence reçu',
        12: 'Colis annulé',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'A rembourser',
        17: 'Remboursé',
        18: 'Planification',
        19: 'Livré Temporaire',
        20: 'Retour Temporaire',
        21: 'Enlevés',
        22: 'Retour Définitif',
        23: 'Relance',
        24: 'Anomalie de livraison',
      }
      return statusMap[status] || 'Inconnu'
    },
    getStatusVariant(status) {
      // Map order status to Bootstrap variant colors
      const variantMap = {
        0: 'warning', // En Attente
        1: 'info', // En Pickup
        2: 'primary', // Colis au dépôt
        3: 'info', // En cours
        4: 'success', // Livré
        5: 'success', // Retour au dépôt
        6: 'success', // Livré payé
        7: 'warning', // Retour au fournisseur
        8: 'info', // En Transfert
        9: 'success', // Retour payé
        10: 'warning', // Retour inter agence
        11: 'success', // Retour inter agence reçu
        12: 'dark', // Colis annulé
        13: 'danger', // Colis perdu
        14: 'warning', // À vérifier
        15: 'secondary', // En attente de collecte
        16: 'warning', // A rembourser
        17: 'success', // Remboursé
        18: 'info', // Planification
        19: 'light-success', // Livré Temporaire
        20: 'light-danger', // Retour Temporaire
        21: 'primary', // Enlevés
        22: 'dark', // Retour Définitif
        23: 'warning', // Relance
        24: 'danger', // Anomalie de livraison
      }
      return variantMap[status] || 'secondary'
    },
  },
}
</script>
<style lang="scss" >
/* Permanent Return Row Highlighting */
.permanent-return-row {
  background-color: #fee !important;
  border-left: 4px solid #f00 !important;
}

.permanent-return-info {
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0%, 100% {
    background-color: #fee;
    border-color: #f00;
  }
  50% {
    background-color: #fcc;
    border-color: #c00;
  }
}

/* Driver Performance Card Styles */
.driver-performance-card {
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}
.driver-performance-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}
.driver-performance-card .card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e9ecef;
  border-radius: 12px 12px 0 0;
}
.performance-stat {
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.performance-stat:hover {
  background-color: #f8f9fa;
  transform: scale(1.05);
}
.performance-stat .stat-icon {
  opacity: 0.8;
}
.performance-stat .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 0.25rem;
}
.performance-stat .stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.performance-progress .progress {
  border-radius: 10px;
  background-color: #f1f3f4;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}
.performance-progress .progress-bar {
  border-radius: 10px;
  transition: width 0.6s ease;
}
.performance-indicator {
  padding: 0.5rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  background-color: rgba(115, 103, 240, 0.05);
  border-left: 3px solid rgba(115, 103, 240, 0.3);
}
.performance-indicator:last-child {
  margin-bottom: 0;
}
.exchange-info {
  border-left: 4px solid #ffc107;
  background: linear-gradient(135deg, #fff3cd 0%, #fef7e0 100%);
  border-radius: 6px !important;
}
@media (max-width: 768px) {
  .performance-stat .temp-return-details {
    padding-left: 1.75rem;
  }
}

.no-driver-alert {
  background: rgba(255, 159, 67, 0.14) !important;
  border-color: rgba(255, 159, 67, 0.35) !important;
  color: #b45b00 !important;

  .feather-icon {
    color: #f59e0b !important;
  }

  .font-weight-bold {
    color: #c46a00;
  }

  .text-muted {
    color: #b18252 !important;
  }
}

.return-reason-buttons {
  display: flex;
  flex-wrap: wrap;

  .btn {
    border-radius: 999px !important;
    border-color: #d0d7de !important;
    background: linear-gradient(135deg, #f6f8fa 0%, #eef1f4 100%) !important;
    color: #222 !important;
    font-weight: 600;
    padding: 0.35rem 0.9rem !important;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
    transition: all 0.18s ease;

    &:hover {
      background: linear-gradient(135deg, #eef1f4 0%, #e2e8ee 100%) !important;
      border-color: #b8c2cc !important;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.25);
    }
  }

  .btn-success {
    background: linear-gradient(135deg, #2ad19f 0%, #1ea37b 100%) !important;
    border-color: #1ea37b !important;
    color: #fff !important;

    &:hover {
      background: linear-gradient(135deg, #26c393 0%, #188a67 100%) !important;
      border-color: #188a67 !important;
    }
  }
}

.applied-filters {
  .badge {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }
}
.performance-indicator {
    margin-bottom: 0.75rem;
  }
  .driver-performance-card {
    margin-bottom: 1rem;
  }
  /* French datetime picker improvements */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}
.performance-stat.updated {
  animation: pulse 0.5s ease-in-out;
}
.driver-performance-card.border-success {
  border-color: #28a745 !important;
  border-width: 2px;
}
.driver-performance-card.border-warning {
  border-color: #ffc107 !important;
  border-width: 2px;
}
.driver-performance-card.border-danger {
  border-color: #dc3545 !important;
  border-width: 2px;
}
.badge-glow {
  box-shadow: 0 0 10px rgba(115, 103, 240, 0.5);
  animation: badgeGlow 2s ease-in-out infinite alternate;
}
@keyframes badgeGlow {
  from {
    box-shadow: 0 0 5px rgba(115, 103, 240, 0.5);
  }
  to {
    box-shadow: 0 0 15px rgba(115, 103, 240, 0.8);
  }
}
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';
.b-modal .modal-header .close,
  .b-modal .modal-footer {
    display: none !important;
  }
  .exchange-row {
    background-color: red;
    color: white;
    padding:50px
  }

  // Enhanced return reason UI styles
  .return-reason-container {
    min-width: 250px;
    max-width: 350px;

    .form-control, .form-select {
      border-radius: 4px;
      font-size: 0.875rem;
    }

    .input-group-text {
      background-color: #f8f9fa;
      border-color: #ced4da;
      padding: 0.25rem 0.5rem;
    }

    textarea.form-control {
      resize: vertical;
      min-height: 60px;
    }

    .form-check {
      font-size: 0.8rem;

      .form-check-input {
        margin-top: 0.125rem;
      }

      .form-check-label {
        line-height: 1.2;
        margin-left: 0.25rem;
      }
    }

    // Animation for expanding sections
    .mt-1 {
      animation: slideDown 0.2s ease-out;
    }

    // Special styling for client date requirements
    .client-date-highlight {
      .input-group-text {
        background-color: #fff3cd !important;
        border-color: #ffc107 !important;
        color: #856404 !important;
      }

      .form-control {
        border-color: #ffc107 !important;
        box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25) !important;

        &:focus {
          border-color: #ffc107 !important;
          box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.5) !important;
        }
      }
    }
  }

  /* Enhanced return reason styles */
.client-date-highlight {
  border: 2px solid #ffc107 !important;
  box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25);
}

.client-date-highlight input {
  background-color: #fff3cd;
}

/* French datetime picker improvements */
input[type="datetime-local"] {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

input[type="datetime-local"]:lang(fr),
input[type="datetime-local"]:lang(fr-FR),
input[type="datetime-local"][lang="fr-FR"] {
  /* Improve French date formatting */
  letter-spacing: 0.5px;
}

/* Ensure proper French locale rendering */
input[type="datetime-local"]:lang(fr-FR)::-webkit-datetime-edit-text,
input[type="datetime-local"][lang="fr-FR"]::-webkit-datetime-edit-text {
  padding: 0 0.2em;
}

/* Better spacing for return reason enhanced fields */
.return-reason-enhanced {
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  padding: 8px;
  margin-top: 4px;
  border-radius: 4px;
}

.return-reason-enhanced.client-date {
  border-left-color: #ffc107;
  background-color: #fff3cd;
}

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // Responsive adjustments for return reason table
  @media (max-width: 768px) {
    .return-reason-container {
      min-width: 200px;
      max-width: 280px;
    }
  }

  // Payment confirmation modal styles
  .payment-summary {
    .b-card {
      border-radius: 8px;

      &.variant-light {
        background-color: #f8f9fa;
        border: 1px solid #e9ecef;
      }

      &.variant-primary {
        background: linear-gradient(135deg, #7367f0 0%, #5e50ee 100%);
        border: none;
      }
    }

    h6 {
      font-weight: 600;
      font-size: 0.9rem;
    }

    .total-payment {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      padding: 1rem;
      border-radius: 8px;
      border: 2px solid #7367f0;

      h5 {
        margin: 0;
        font-weight: 700;
      }
    }
  }

  // Modal customizations
  .modal-lg {
    .modal-content {
      border-radius: 12px;
      border: none;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
      border-bottom: 1px solid #e9ecef;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 12px 12px 0 0;

      .modal-title {
        color: #5e5873;
        font-weight: 600;
      }
    }

    .modal-footer {
      border-top: 1px solid #e9ecef;
      background-color: #f8f9fa;
      border-radius: 0 0 12px 12px;
    }
  }
</style>
